import { DateTime } from 'luxon';
import * as React from 'react';
import styled, { css } from 'styled-components';
import { useCalendar } from '../Calendar.context';
import { CalendarDayInfo } from './calendar-day-info';
import { CalendarDayButton } from './CalendarDayButton';
import { CalendarDayCell } from './CalendarDayCell';
import { DayRange } from './DayRange';

export interface CalendarDayProps {
    day: CalendarDayInfo;
    children: React.ReactNode;
    onClick?: (day: CalendarDayInfo) => void;
    onHover?: (day: CalendarDayInfo) => void;
    onHoverExit?: () => void;
}

const SelectedRange = styled(DayRange)`
    background-color: ${({ theme }) => theme.palette.primary.main};
    opacity: 0.2;
`;
const PreviewRange = styled(DayRange)`
    border: 1px dashed transparent;
    border-bottom-color: ${({ theme }) => theme.palette.primary.main};
    border-top-color: ${({ theme }) => theme.palette.primary.main};
    ${({ $shouldStopLeft, theme }) =>
        $shouldStopLeft &&
        css`
            border-left-color: ${theme.palette.primary.main};
        `}
    ${({ $shouldStopRight, theme }) =>
        $shouldStopRight &&
        css`
            border-right-color: ${theme.palette.primary.main};
        `}
`;

/**
 * An element in a calendar.  If provided with data from one of the calendar hooks, will
 * display selected, preview, today, and hovered correctly.
 *
 * @param props - CalendarDayProps
 * @returns An element in a calendar
 */
export function CalendarDay(props: CalendarDayProps): JSX.Element {
    const { decrementMonth, incrementMonth, setHoveredDate } = useCalendar();
    const ref = React.useRef<HTMLButtonElement>(null);
    React.useEffect(() => {
        if (props.day.isSelected) {
            ref.current?.focus();
        }
    }, [props.day.isSelected]);
    React.useEffect(() => {
        if (props.day.isHovered) {
            ref.current?.focus();
        }
    }, [props.day.isHovered]);
    const handleKeyPress = (ev: React.KeyboardEvent) => {
        if (ev.key === 'ArrowUp') {
            setHoveredDate?.(DateTime.fromJSDate(props.day.date).minus({ weeks: 1 }).toJSDate());
        }
        if (ev.key === 'ArrowDown') {
            setHoveredDate?.(DateTime.fromJSDate(props.day.date).plus({ weeks: 1 }).toJSDate());
        }
        if (ev.key === 'ArrowLeft') {
            if (DateTime.fromJSDate(props.day.date).weekday === 1) {
                decrementMonth?.();
                setHoveredDate?.(DateTime.fromJSDate(props.day.date).minus({ month: 1 }).startOf('week').toJSDate());
            } else {
                setHoveredDate?.(DateTime.fromJSDate(props.day.date).minus({ days: 1 }).toJSDate());
            }
        }
        if (ev.key === 'ArrowRight') {
            if (DateTime.fromJSDate(props.day.date).weekday === 7) {
                incrementMonth?.();
                setHoveredDate?.(DateTime.fromJSDate(props.day.date).plus({ month: 1 }).endOf('week').toJSDate());
            } else {
                setHoveredDate?.(DateTime.fromJSDate(props.day.date).plus({ days: 1 }).toJSDate());
            }
        }
    };
    return (
        <CalendarDayCell>
            {props.day.isBetweenSelected && (
                <SelectedRange
                    $shouldStopLeft={props.day.isFirstDayOfWeek || props.day.isSelected}
                    $shouldStopRight={props.day.isLastDayOfWeek || props.day.isSelectedTo}
                />
            )}
            {props.day.isBetweenHovered && (
                <PreviewRange
                    $shouldStopLeft={props.day.isFirstDayOfWeek || props.day.isHoveredStart}
                    $shouldStopRight={props.day.isLastDayOfWeek || props.day.isHoveredEnd}
                />
            )}
            <CalendarDayButton
                $day={props.day}
                ref={ref}
                tabIndex={0}
                onClick={() => props.onClick?.(props.day)}
                onKeyDown={handleKeyPress}
                onMouseEnter={() => props.onHover?.(props.day)}
                onMouseLeave={props.onHoverExit}
            >
                {props.children}
            </CalendarDayButton>
        </CalendarDayCell>
    );
}
CalendarDay.displayName = 'CalendarDay';
